/**
 * Per-tour video uploads — used by the dashboard's Developer Mode panel
 * on the Video tab.
 *
 * Upload path: the FE streams the file straight to Firebase Storage via
 * `uploadBytesResumable` (handles chunking + resume for big files), then
 * pings the `tourVideosSave` GCF to make the object public and append it
 * to gs://{bucket}/tours/{tourId}/videos.json. The GCF only sees the
 * metadata, so the 32 MB Cloud Functions body limit is irrelevant.
 *
 * Storage shape (unchanged):
 *   - Index:  gs://{bucket}/tours/{tourId}/videos.json
 *   - Videos: gs://{bucket}/tours/{tourId}/videos/{videoId}.{ext}
 */

import {
  getStorage,
  ref as storageRef,
  uploadBytesResumable,
} from 'firebase/storage';

const STORAGE_BUCKET =
  process.env.REACT_APP_STORAGE_BUCKET || 'id3d-prod.appspot.com';

export interface TourVideo {
  id: string;
  url: string;
  uploadedAt?: string;
  filename?: string | null;
  contentType?: string;
  sizeBytes?: number;
  caption?: string;
}

function baseUrl(): string {
  const url = process.env.REACT_APP_VIDEOS_API_BASE_URL;
  if (!url) {
    throw new Error('REACT_APP_VIDEOS_API_BASE_URL is not configured');
  }
  return url.replace(/\/$/, '');
}

function stripPrefix(tourId: string): string {
  return tourId.startsWith('tours/') ? tourId.slice(6) : tourId;
}

/**
 * Map a video MIME type → file extension. Falls back to the source file's
 * extension when the MIME isn't one we know (e.g. video/x-matroska).
 */
function extensionFor(file: File): string {
  const mime = file.type;
  if (/mp4/.test(mime)) return 'mp4';
  if (/webm/.test(mime)) return 'webm';
  if (/quicktime/.test(mime)) return 'mov';
  if (/ogg/.test(mime)) return 'ogv';
  const m = /\.([a-z0-9]+)$/i.exec(file.name);
  return m ? m[1].toLowerCase() : 'mp4';
}

/**
 * Soft cap so the UI can warn before a 5-minute upload starts. Firebase
 * Storage itself accepts files up to 5 TB, so this is a UX guard rather
 * than a hard backend limit.
 */
export const MAX_VIDEO_UPLOAD_BYTES = 500 * 1024 * 1024;

export interface UploadProgress {
  bytesTransferred: number;
  totalBytes: number;
  /** 0..1 fraction complete. */
  fraction: number;
}

export async function uploadTourVideo(
  tourId: string,
  file: File,
  options: {
    caption?: string;
    onProgress?: (p: UploadProgress) => void;
  } = {},
): Promise<TourVideo> {
  if (!file.type.startsWith('video/')) {
    throw new Error('File must be a video');
  }
  if (file.size > MAX_VIDEO_UPLOAD_BYTES) {
    throw new Error(
      `Video must be ${MAX_VIDEO_UPLOAD_BYTES / 1024 / 1024} MB or smaller`,
    );
  }

  const id = stripPrefix(tourId);
  const ext = extensionFor(file);
  const videoId = `video_${Date.now()}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;
  const objectPath = `tours/${id}/videos/${videoId}.${ext}`;

  const storage = getStorage();
  const objectReference = storageRef(storage, objectPath);
  const task = uploadBytesResumable(objectReference, file, {
    contentType: file.type,
  });

  // Promise-ify the upload + thread progress callbacks through.
  await new Promise<void>((resolve, reject) => {
    task.on(
      'state_changed',
      (snap) => {
        options.onProgress?.({
          bytesTransferred: snap.bytesTransferred,
          totalBytes: snap.totalBytes,
          fraction:
            snap.totalBytes > 0 ? snap.bytesTransferred / snap.totalBytes : 0,
        });
      },
      (err) => reject(err),
      () => resolve(),
    );
  });

  // Register with the GCF so the object is made public + indexed.
  const res = await fetch(`${baseUrl()}/tourVideosSave`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tourId: id,
      videoId,
      objectPath,
      filename: file.name,
      contentType: file.type,
      sizeBytes: file.size,
      caption: options.caption,
    }),
  });
  if (!res.ok) {
    let detail = '';
    try {
      detail = (await res.json())?.error || '';
    } catch {
      /* ignore */
    }
    throw new Error(detail || `tour videos POST returned ${res.status}`);
  }
  const body = (await res.json()) as { video: TourVideo };
  return body.video;
}

export async function getTourVideos(tourId: string): Promise<TourVideo[]> {
  const id = stripPrefix(tourId);
  const url = `https://storage.googleapis.com/${STORAGE_BUCKET}/tours/${id}/videos.json?_=${Date.now()}`;
  const res = await fetch(url);
  if (res.status === 404) return [];
  if (!res.ok) {
    throw new Error(`tour videos GET returned ${res.status} ${res.statusText}`);
  }
  const data = (await res.json()) as { videos?: TourVideo[] };
  return Array.isArray(data.videos) ? data.videos : [];
}

export async function deleteTourVideo(
  tourId: string,
  videoId: string,
): Promise<void> {
  const res = await fetch(`${baseUrl()}/tourVideosDelete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tourId: stripPrefix(tourId), videoId }),
  });
  if (!res.ok) {
    let detail = '';
    try {
      detail = (await res.json())?.error || '';
    } catch {
      /* ignore */
    }
    throw new Error(detail || `tour video DELETE returned ${res.status}`);
  }
}
